import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { docCirrusRpcClient } from "../../../lib/rpc/doc-cirrus.client";
import { usePlatform } from "../../../platform/context";
import { reportOperationalError } from "../../../lib/operational-error-telemetry";
import {
  useSelfServiceCardReadPending,
  useSelfServiceExternalInput,
} from "../context";
import {
  CARD_DATA_UNAVAILABLE_TAG,
  cardReadFailureTag,
} from "./card-read-failure";

/**
 * Visual/session phase exposed by the shared card-reader terminal hook.
 *
 * `card-unreadable` means the terminal holds an eGK but inSuite returned no patient data for it.
 * It is a distinct, truthful state: the card is present, nothing was fabricated, and automatic
 * re-reading pauses until the patient retries or removes the card.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * const isBusy = cardReader.phase === 'reading'
 * ```
 */
export type CardReaderPhase =
  | "loading"
  | "no-terminal"
  | "checking"
  | "no-card"
  | "card-detected"
  | "card-unreadable"
  | "reading"
  | "error";

type DetectedCard = Awaited<
  ReturnType<typeof docCirrusRpcClient.frontdesk.getCards>
>[number];
type CardReadResult = Awaited<
  ReturnType<typeof docCirrusRpcClient.frontdesk.readCard>
>;

/**
 * Context passed to a flow-specific card-read handler.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * const handleRead = async ({ readCard }: CardReaderReadContext) => {
 *   const result = await readCard()
 *   // Map the card read into the current flow machine.
 * }
 * ```
 */
export interface CardReaderReadContext {
  terminalId: string;
  detectedCards: DetectedCard[];
  readCard: () => Promise<CardReadResult>;
  resetAutoRead: () => void;
  setPhase: Dispatch<SetStateAction<CardReaderPhase>>;
  setErrorMessage: Dispatch<SetStateAction<string | null>>;
}

interface CardReaderErrorContext {
  terminalId: string | null;
}

interface UseCardReaderSessionOptions {
  enabled: boolean;
  settingsErrorMessage: string;
  /** Shown when an inserted eGK yields no patient data from inSuite. */
  unreadableCardMessage: string;
  /** Shown when a card read fails for any other reason. */
  readErrorMessage: string;
  autoReadDelayMs?: number;
  onReadCard: (context: CardReaderReadContext) => Promise<void> | void;
  onReadError?: (error: unknown, context: CardReaderErrorContext) => void;
  onPollError?: (error: unknown, context: CardReaderErrorContext) => void;
}

/**
 * Manages a FrontDesk card-reader terminal session for self-service flows.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * const cardReader = useCardReaderSession({
 *   enabled: mode === 'card',
 *   settingsErrorMessage: t('self-service.check-in.card-check.settings-error'),
 *   onReadCard: async ({ readCard }) => {
 *     const result = await readCard()
 *     // Map the result into the owning flow machine.
 *   },
 * })
 * ```
 */
export function useCardReaderSession({
  enabled,
  settingsErrorMessage,
  unreadableCardMessage,
  readErrorMessage,
  autoReadDelayMs = 0,
  onReadCard,
  onReadError,
  onPollError,
}: UseCardReaderSessionOptions) {
  useSelfServiceExternalInput("health-card", enabled);
  const platform = usePlatform();
  const [phase, setPhase] = useState<CardReaderPhase>("loading");
  useSelfServiceCardReadPending(enabled && phase === "reading");
  const [detectedCards, setDetectedCards] = useState<DetectedCard[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [connectionWarning, setConnectionWarning] = useState(false);

  const terminalIdRef = useRef<string | null>(null);
  const detectedCardsRef = useRef<DetectedCard[]>([]);
  const autoReadTriggeredRef = useRef(false);
  const autoReadTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const failureCountRef = useRef(0);
  const readInFlightRef = useRef(false);
  const pollInFlightRef = useRef(false);
  const onReadCardRef = useRef(onReadCard);
  const onReadErrorRef = useRef(onReadError);
  const onPollErrorRef = useRef(onPollError);
  const sessionGeneration = useRef(0);
  const readAbort = useRef<AbortController | undefined>(undefined);

  useEffect(function ownReadSession() {
    sessionGeneration.current += 1;
    return function releaseReadSession() {
      sessionGeneration.current += 1;
      readAbort.current?.abort();
    };
  }, [enabled]);

  onReadCardRef.current = onReadCard;
  onReadErrorRef.current = onReadError;
  onPollErrorRef.current = onPollError;

  const clearScheduledAutoRead = useCallback(() => {
    if (autoReadTimeoutRef.current) {
      clearTimeout(autoReadTimeoutRef.current);
      autoReadTimeoutRef.current = null;
    }
  }, []);

  const resetAutoRead = useCallback(() => {
    autoReadTriggeredRef.current = false;
    clearScheduledAutoRead();
  }, [clearScheduledAutoRead]);

  const readDetectedCard = useCallback(async () => {
    const terminalId = terminalIdRef.current;
    if (!enabled || !terminalId || readInFlightRef.current) return;

    readInFlightRef.current = true;
    autoReadTriggeredRef.current = true;
    clearScheduledAutoRead();
    setPhase("reading");
    const generation = sessionGeneration.current;
    const readController = new AbortController();
    readAbort.current = readController;

    try {
      await onReadCardRef.current({
        terminalId,
        detectedCards: detectedCardsRef.current,
        readCard: async () => {
          const result = await docCirrusRpcClient.frontdesk.readCard({ terminalId }, { signal: readController.signal });
          if (sessionGeneration.current !== generation) {
            await docCirrusRpcClient.frontdesk.abandonCardRead({ crlogId: result.crlogId });
            throw new Error("Card read session has ended");
          }
          return result;
        },
        resetAutoRead,
        setPhase,
        setErrorMessage,
      });
    } catch (error) {
      if (sessionGeneration.current !== generation) return;
      void reportOperationalError({
        action: "card-reader.read-handler",
        severity: "error",
        source: "lifecycle",
        transport: "effect-rpc-http",
        component: "useCardReaderSession",
        error,
      });
      onReadErrorRef.current?.(error, { terminalId });
      if (cardReadFailureTag(error) === CARD_DATA_UNAVAILABLE_TAG) {
        // The card stays inserted; keep the auto-read trigger consumed so the same card is not
        // re-read until the patient explicitly retries.
        setErrorMessage(unreadableCardMessage);
        setPhase("card-unreadable");
        return;
      }
      setErrorMessage(readErrorMessage);
      setPhase("card-detected");
    } finally {
      if (readAbort.current === readController) readAbort.current = undefined;
      readInFlightRef.current = false;
    }
  }, [
    enabled,
    clearScheduledAutoRead,
    readErrorMessage,
    resetAutoRead,
    unreadableCardMessage,
  ]);

  const retry = useCallback(() => {
    if (readInFlightRef.current) return;
    failureCountRef.current = 0;
    autoReadTriggeredRef.current = false;
    clearScheduledAutoRead();
    setConnectionWarning(false);
    setErrorMessage(null);
    setPhase(terminalIdRef.current ? "checking" : "no-terminal");
  }, [clearScheduledAutoRead]);

  const scheduleAutoRead = useCallback(() => {
    clearScheduledAutoRead();

    if (autoReadDelayMs <= 0) {
      void readDetectedCard();
      return;
    }

    autoReadTimeoutRef.current = setTimeout(() => {
      autoReadTimeoutRef.current = null;
      void readDetectedCard();
    }, autoReadDelayMs);
  }, [autoReadDelayMs, clearScheduledAutoRead, readDetectedCard]);

  useEffect(
    function loadSelectedTerminal() {
      let isMounted = true;

      void (async () => {
        try {
          const settings = await platform.getPracticeSettings();
          if (!isMounted) return;

          if (settings.selectedTerminalId) {
            terminalIdRef.current = settings.selectedTerminalId;
            failureCountRef.current = 0;
            setPhase("checking");
            return;
          }

          terminalIdRef.current = null;
          detectedCardsRef.current = [];
          setDetectedCards([]);
          setPhase("no-terminal");
        } catch (error) {
          if (!isMounted) return;

          void reportOperationalError({
            action: "card-reader.settings-load",
            severity: "error",
            source: "native",
            component: "useCardReaderSession",
            error,
          });

          terminalIdRef.current = null;
          detectedCardsRef.current = [];
          setDetectedCards([]);
          setPhase("no-terminal");
          setErrorMessage(settingsErrorMessage);
        }
      })();

      return function stopTerminalLoad() {
        isMounted = false;
        clearScheduledAutoRead();
      };
    },
    [clearScheduledAutoRead, platform, settingsErrorMessage],
  );

  useEffect(
    function pollForCards() {
      if (
        !enabled ||
        !terminalIdRef.current ||
        phase === "reading" ||
        phase === "no-terminal" ||
        phase === "loading" ||
        phase === "error"
      ) {
        return;
      }

      let isMounted = true;

      const checkForCards = async () => {
        const terminalId = terminalIdRef.current;
        if (!terminalId || pollInFlightRef.current || readInFlightRef.current)
          return;
        pollInFlightRef.current = true;

        try {
          const cards = await docCirrusRpcClient.frontdesk.getCards({
            terminalId,
          });
          if (!isMounted) return;
          failureCountRef.current = 0;
          setConnectionWarning(false);

          if (cards && cards.length > 0) {
            const nextCards = [...cards];
            detectedCardsRef.current = nextCards;
            setDetectedCards(nextCards);

            // An unreadable card that is still inserted keeps its truthful state; only
            // removal or an explicit retry leaves it.
            if (phase === "card-unreadable") return;

            // A failed read leaves its message visible while the same card stays inserted;
            // entering card-detected from another phase starts clean.
            if (phase !== "card-detected") {
              setPhase("card-detected");
              setErrorMessage(null);
            }

            if (!autoReadTriggeredRef.current) {
              autoReadTriggeredRef.current = true;
              scheduleAutoRead();
            }
            return;
          }

          detectedCardsRef.current = [];
          setDetectedCards([]);
          setPhase("no-card");
          setErrorMessage(null);
          autoReadTriggeredRef.current = false;
          clearScheduledAutoRead();
        } catch (error) {
          if (!isMounted) return;

          onPollErrorRef.current?.(error, { terminalId });
          failureCountRef.current += 1;
          if (failureCountRef.current === 3) {
            void reportOperationalError({
              action: "card-reader.poll-offline",
              severity: "error",
              source: "lifecycle",
              transport: "effect-rpc-http",
              component: "useCardReaderSession",
              error,
              context: {
                consecutiveFailures: failureCountRef.current,
              },
            });
          }
          detectedCardsRef.current = [];
          setDetectedCards([]);
          setPhase("no-card");
          setConnectionWarning(failureCountRef.current >= 3);
        } finally {
          pollInFlightRef.current = false;
        }
      };

      void checkForCards();
      const interval = setInterval(checkForCards, 2000);

      return function stopCardPolling() {
        isMounted = false;
        clearInterval(interval);
        clearScheduledAutoRead();
      };
    },
    [clearScheduledAutoRead, enabled, phase, scheduleAutoRead],
  );

  return {
    phase,
    detectedCards,
    errorMessage,
    connectionWarning,
    readCard: readDetectedCard,
    retry,
  };
}
